import { useEffect } from "react";
import { Zap, ShieldCheck, Infinity as InfinityIcon, BookOpen, Check, ArrowRight } from "lucide-react";
import { useLang } from "../components/Layout";
import { Reveal, Label, GoldLink, Diamond } from "../components/Reveal";
import { STRIPE_URL } from "../i18n";

const badgeIcons = [Zap, ShieldCheck, InfinityIcon];

const KitPage = () => {
  const { lang, t } = useLang();
  useEffect(() => {
    document.title = t.pageTitles.kit;
    document.documentElement.lang = lang;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", t.pageDescs.kit);
  }, [lang, t]);

  return (
    <>
      <section data-testid="kit-hero" className="relative overflow-hidden border-b border-white/10 pt-40 pb-20 md:pb-28">
        <div
          aria-hidden="true"
          className="absolute -right-40 top-20 h-[480px] w-[480px] rounded-full border border-brand-gold/15"
        >
          <div className="absolute inset-12 rounded-full border border-dashed border-brand-gold/15" />
        </div>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal y={24}>
            <Label testId="kit-label">{t.kit.label}</Label>
            <h1 className="mt-6 max-w-4xl font-serif text-4xl sm:text-6xl font-light leading-[1.08] text-white">
              {t.kit.title}
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg font-light leading-relaxed text-white/60">{t.kit.sub}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              {t.kit.badges.map((b, i) => {
                const Icon = badgeIcons[i];
                return (
                  <span
                    key={b}
                    data-testid={`kit-badge-${i}`}
                    className="inline-flex items-center gap-2 border border-white/15 bg-brand-surface px-4 py-2 text-xs font-light text-white/70"
                  >
                    <Icon size={14} className="text-brand-gold" /> {b}
                  </span>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="kit-tools-section" className="border-b border-white/10 bg-brand-surface/40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
          <Reveal>
            <Label testId="kit-tools-label">{t.kit.toolsLabel}</Label>
            <h2 className="mt-6 max-w-3xl font-serif text-3xl sm:text-5xl font-light leading-tight text-white">
              {t.kit.toolsTitle}
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {t.kit.tools.map((tool, i) => (
              <Reveal key={tool.name} delay={i * 0.08} className="h-full">
                <div
                  data-testid={`kit-tool-${i + 1}`}
                  className="group flex h-full flex-col border border-white/10 bg-brand-ink p-8 transition-all duration-300 hover:border-brand-gold/50 hover:-translate-y-1"
                >
                  <span className="font-serif text-3xl font-light text-brand-gold/40">0{i + 1}</span>
                  <h3 className="mt-6 font-serif text-xl font-light leading-snug text-white">{tool.name}</h3>
                  <p className="mt-3 text-sm font-light text-white/50">{tool.tag}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="kit-book-section" className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <Reveal>
              <div className="relative overflow-hidden border border-brand-gold/30 bg-gradient-to-b from-brand-elevated to-brand-ink p-10 md:p-14 shadow-2xl">
                <BookOpen size={28} className="text-brand-gold" />
                <p className="mt-8 font-serif text-2xl md:text-3xl font-light leading-snug text-white">
                  {t.kit.tools[0].name}
                </p>
                <p className="mt-4 uppercase tracking-luxe text-[10px] text-brand-gold">{t.kit.tools[0].tag}</p>
                <div className="pointer-events-none absolute -bottom-20 -right-20 h-56 w-56 rounded-full border border-brand-gold/15" />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <Label testId="kit-book-label">{t.kit.bookLabel}</Label>
              <h2 className="mt-6 font-serif text-3xl sm:text-4xl font-light leading-tight text-white">
                {t.kit.bookTitle}
              </h2>
              {t.kit.bookCopy.map((p, i) => (
                <p key={i} className="mt-6 text-base font-light leading-relaxed text-white/60">
                  {p}
                </p>
              ))}
              <ul className="mt-8 space-y-3">
                {t.kit.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm font-light text-white/70">
                    <Check size={16} className="mt-0.5 shrink-0 text-brand-gold" /> {b}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="kit-cta-section" className="bg-brand-surface/40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-28 md:py-36">
          <Reveal className="text-center">
            <Diamond className="text-sm" />
            <h2 className="mx-auto mt-8 max-w-3xl font-serif text-3xl sm:text-5xl font-light leading-tight text-white">
              {t.kit.ctaTitle}
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base font-light leading-relaxed text-white/60">{t.kit.ctaSub}</p>
            <div className="mt-12 flex flex-col items-center gap-5">
              <GoldLink testId="kit-stripe-checkout-button" href={STRIPE_URL} className="!px-12 !py-5">
                {t.kit.buy} <ArrowRight size={16} />
              </GoldLink>
              <p data-testid="kit-delivery-note" className="text-xs font-light text-white/40">{t.kit.note}</p>
              <a
                data-testid="kit-downloads-link"
                href={`/${lang}/merci`}
                className="text-xs font-light text-brand-gold/80 underline underline-offset-4 hover:text-brand-gold transition-colors"
              >
                {t.kit.afterLink}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default KitPage;
